import { createHeadlessEditor } from '@lexical/headless';
import { RICH_TEXT_NODES } from '@plato/shared-util/lexical-config';
import { $getRoot, type SerializedEditorState } from 'lexical';

export type LexicalToPlainTextOptions = {
    collapseWhitespace?: boolean;
};

/**
 * Server-side utility to extract plain text from Lexical EditorState.
 *
 * Accepts `unknown` so callers (e.g. Prisma JSON columns) don't need to cast.
 *
 * @param editorState - Lexical SerializedEditorState (or unknown from Prisma JSON columns)
 * @param options - Optional configuration
 * @returns Plain text string or null if no content
 */
export function lexicalToPlainText(editorState: unknown, options?: LexicalToPlainTextOptions): string | null {
    if (!editorState) {
        return null;
    }

    const { collapseWhitespace = true } = options ?? {};

    try {
        const editor = createHeadlessEditor({
            nodes: RICH_TEXT_NODES,
            onError: (error) => {
                console.error('Lexical plain text extraction error:', error);
            },
        });

        /* eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        -- editorState is typed as unknown for caller convenience (e.g. Prisma JSON columns);
        the column always stores SerializedEditorState, and invalid input is caught by the try/catch */
        const parsedEditorState = editor.parseEditorState(editorState as SerializedEditorState);
        editor.setEditorState(parsedEditorState);

        let textContent = '';

        editor.read(() => {
            const root = $getRoot();
            textContent = root.getTextContent();
        });

        const cleanText = collapseWhitespace
            ? textContent.replaceAll(/\s+/g, ' ').trim()
            : textContent.trim();

        return cleanText || null;
    } catch (error) {
        console.error('Error extracting plain text from Lexical:', error);
        return null;
    }
}
